import type { LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Link, Outlet, useMatches, useRouteLoaderData } from "@remix-run/react";
import { HelpFooter } from "~/components/help-center/help-footer";
import { HelpSearchProvider } from "~/components/help-center/help-search";
import Layout from "~/components/layout";
import Logo from "~/components/logo";
import { PromotionBanner } from "~/components/session/promotion-banner";
import { getOptionalAuth } from "~/config/asyncStore.server";
import { scheduleBackgroundTask, withCors } from "~/config/cors";
import { onboardingChecklistRepository } from "~/features/onboarding-checklist/server/repository";
import { captureException } from "~/modules.server/analytics";
import { getReadOnlyModeState } from "~/modules.server/read-only-mode.server";
import { getAllHelpArticles } from "~/utils.server/keystatic-reader";

// AIDEV-NOTE: Help center layout route — public, no auth required.
// Articles are embedded at build time via import.meta.glob (no filesystem reads).
async function markHelpCenterViewed(profileId: string) {
    const state = await onboardingChecklistRepository.getOnboardingState({
        practitionerId: profileId,
    });
    if (state === null || state.helpCenterViewedAt) {
        return;
    }

    await onboardingChecklistRepository.updateOnboardingState({
        practitionerId: profileId,
        patch: { helpCenterViewedAt: new Date().toISOString() },
    });
}

// AIDEV-NOTE: withCors populates the async-store auth context. Without it,
// getOptionalAuth() is undefined on this public route and the help-center
// onboarding item never gets marked. See EVO-1578.
export const loader = withCors(async (_args: LoaderFunctionArgs) => {
    const articles = getAllHelpArticles();
    const auth = getOptionalAuth();
    // AIDEV-NOTE: This loader writes onboarding state. During the write fence the
    // help center must stay readable, so gate the write instead of blocking the route.
    const fenced =
        auth?.authStatus === "authenticated"
            ? (await getReadOnlyModeState()).enabled
            : false;
    if (auth?.authStatus === "authenticated" && !fenced) {
        // AIDEV-NOTE: waitUntil-backed; a bare un-awaited write was frozen by Vercel
        // after the response and died with EPIPE/socket hang up (BACKEND-6M9/6MA/6MB).
        scheduleBackgroundTask(
            markHelpCenterViewed(auth.userId).catch((error) => {
                captureException(
                    "OnboardingChecklist: help center mark failed",
                    error,
                );
            }),
        );
    }
    return json({ articles });
});

export interface HelpArticleNavItem {
    slug: string;
    title: string;
    category: string;
    sortOrder: number;
    summary: string;
    plainText: string;
}

export default function HelpCenterLayout() {
    // AIDEV-NOTE: The loader still returns `articles` — child routes read it via
    // useRouteLoaderData("routes/help"). This layout no longer consumes it
    // directly now that the duplicate sidebar navigation is gone.

    // AIDEV-NOTE: Detect authentication via explicit boolean from root loader.
    // dehydratedState is always set (even for unauthenticated visitors), so we
    // use the explicit isAuthenticated flag instead.
    const rootData = useRouteLoaderData("root") as
        | { isAuthenticated?: boolean }
        | undefined;
    const isAuthenticated = !!rootData?.isAuthenticated;

    // AIDEV-NOTE: Use useMatches to get the article title from child route loader data.
    // Falls back to "Help Center" for the index page.
    const matches = useMatches();
    const childMatch = matches[matches.length - 1];
    const childData = childMatch?.data as { title?: string } | undefined;
    const pageTitle = childData?.title || "Help Center";

    // AIDEV-NOTE: One responsive column for both auth states. Previously each
    // branch had its own bespoke wrapper: the authenticated path nested
    // ResponsiveLayout inside SecondarySidebarProvider, and the unauthenticated
    // path used a hand-rolled flex shell with a fixed-padding main. Both forced
    // content wider than a phone viewport, clipping the page on BOTH edges.
    // The sidebar duplicated navigation the index already provides, so it is gone
    // rather than hidden — one layout, no breakpoint-specific structure to drift.
    // AIDEV-NOTE: text-left below is not redundant — MobileLayout's #layout-body
    // applies `items-center justify-center` to page content, so anything that does
    // not assert alignment inherits centering. That is what centered the article
    // headings and accordion triggers.
    const content = (
        <div className="mx-auto w-full max-w-3xl px-4 pb-10 text-left sm:px-6">
            <Outlet />
            <HelpFooter />
        </div>
    );

    if (isAuthenticated) {
        return (
            <HelpSearchProvider>
                <Layout title={pageTitle}>{content}</Layout>
            </HelpSearchProvider>
        );
    }

    // Unauthenticated: no app chrome, same column.
    return (
        <HelpSearchProvider>
            <div className="min-h-dvh bg-brand-cream-100">
                {/* AIDEV-NOTE: PromotionBanner is the public signup CTA the
                    unauthenticated help flow depends on — it came from the old
                    HelpLayout and must stay. */}
                <PromotionBanner />
                <header className="border-b border-brand-forest-100 bg-white">
                    <div className="mx-auto flex w-full max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
                        {/* AIDEV-NOTE: Logo renders its own <Link> (see components/logo.tsx)
                            — do not wrap it in another, that nests anchors and breaks
                            hydration. Pass `to` instead. */}
                        <Logo size="sm" variant="image" to="/" />
                        <h1 className="font-display text-lg font-semibold text-brand-forest-800">
                            {pageTitle}
                        </h1>
                    </div>
                </header>
                {content}
            </div>
        </HelpSearchProvider>
    );
}
